import {
  FloatingPortal,
  type Placement,
  autoUpdate,
  flip,
  offset as offsetMiddleware,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react'
import {cn} from '@tempmee/core'
import {type ReactNode, forwardRef} from 'react'
import {PopoverVariantsFactory} from './Variants'

export interface PopoverProps {
  /** Whether the popover is visible. Controlled — pair with `onClose`. */
  open: boolean
  /** The element the popover anchors to and is positioned against (MUI `anchorEl`). */
  anchorEl: HTMLElement | null
  /** Called when the popover requests to close — an outside click or the Escape key. */
  onClose?: () => void
  /** Preferred placement relative to the anchor (auto-flips to stay in view). @default 'bottom' */
  placement?: Placement
  /** Gap in px between the anchor and the popover. @default 8 */
  offset?: number
  /** Popover body content. */
  children: ReactNode
  /**
   * Surface treatment. `default` is the authored atom (1px `info` teal hairline);
   * `menu` is borderless + elevated, matching the `Menu` surface — use it when the
   * popover holds a menu on the dark brand shell, where the teal hairline reads as
   * an off-brand blue. @default 'default'
   */
  surface?: 'default' | 'menu'
  /** Extra classes merged onto the surface (e.g. a width override). */
  className?: string
}

/**
 * Popover — anchored floating content (MUI `Popover` contract).
 *
 * A controlled, floating-ui–positioned surface: pass `open` + `anchorEl`, and it
 * portals to `<body>`, flips/shifts to stay in view, and closes on an outside
 * click or Escape via `onClose`. Replaces the legacy `TutorialBubble`.
 *
 * @see https://mui.com/material-ui/react-popover
 */
export const Popover = forwardRef<HTMLDivElement, PopoverProps>(
  (
    {
      open,
      anchorEl,
      onClose,
      placement = 'bottom',
      offset = 8,
      surface = 'default',
      children,
      className,
    },
    forwardedRef,
  ) => {
    const {refs, floatingStyles, context} = useFloating({
      open,
      onOpenChange: (next) => {
        if (!next) onClose?.()
      },
      placement,
      elements: {reference: anchorEl},
      whileElementsMounted: autoUpdate,
      middleware: [offsetMiddleware(offset), flip(), shift({padding: 8})],
    })

    const dismiss = useDismiss(context)
    const role = useRole(context, {role: 'dialog'})
    const {getFloatingProps} = useInteractions([dismiss, role])
    const {isMounted, styles: transitionStyles} = useTransitionStyles(context, {duration: 150})

    const mergedRef = useMergeRefs([refs.setFloating, forwardedRef])

    if (!isMounted) return null

    return (
      <FloatingPortal>
        <div
          ref={mergedRef}
          style={{...floatingStyles, ...transitionStyles}}
          className={cn(PopoverVariantsFactory({surface}), className)}
          {...getFloatingProps()}
        >
          {children}
        </div>
      </FloatingPortal>
    )
  },
)

Popover.displayName = 'Popover'
